import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useMenu, type Product, localizedName, localizedDescription } from "../data/menu";
import { cartTotal, useStore } from "../store/useStore";
import { LoteriaCard } from "../components/LoteriaCard";
import { TicketStub } from "../components/TicketStub";
import { ReceiptRoll } from "../components/ReceiptRoll";
import { t } from "../lib/i18n";
import { uuid } from "../lib/uuid";
import { LANGUAGES } from "../data/languages";

type Pending = { p: Product; qty: number; mode: "add" | "edit" };

const CARD_ROT = [-1.5, 1.2, -0.8, 1.5, -1.2, 0.7];

export default function Menu() {
  const lang = useStore((s) => s.language);
  const cart = useStore((s) => s.cart);
  const setQty = useStore((s) => s.setQty);
  const addToCart = useStore((s) => s.addToCart);
  const reset = useStore((s) => s.reset);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const joinTabId = searchParams.get("tab");
  const joinTabToken = searchParams.get("t");
  const [submittingJoin, setSubmittingJoin] = useState(false);

  const submitToTab = async () => {
    if (!joinTabId || cart.length === 0) return;
    setSubmittingJoin(true);
    try {
      const tokQ = joinTabToken ? `?t=${encodeURIComponent(joinTabToken)}` : "";
      for (const line of cart) {
        const idem = uuid();
        const r = await fetch(`/api/t/${encodeURIComponent(joinTabId)}/items${tokQ}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Idempotency-Key": idem },
          body: JSON.stringify({ product_sku: line.sku, qty: line.qty }),
        });
        if (!r.ok) throw new Error(`Item ${line.sku}: ${r.status}`);
      }
      reset();
      navigate(`/kiosk/thanks?join=${encodeURIComponent(joinTabId.slice(-6))}`);
    } catch (e: any) {
      alert("Error al agregar a la cuenta: " + e.message);
    } finally {
      setSubmittingJoin(false);
    }
  };
  const isRtl = LANGUAGES.find((l) => l.code === lang)?.rtl;
  // Live DB menu (admin-edited, available-only — 86'd items never render)
  const { categories: CATEGORIES, products: PRODUCTS, loaded: menuLoaded } = useMenu();
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [confirmRestart, setConfirmRestart] = useState(false);

  const currentCat = activeCat ?? CATEGORIES[0]?.id ?? "";
  const items = PRODUCTS.filter((p) => p.category_id === currentCat && p.available);
  const total = cartTotal(cart);
  const totalQty = cart.reduce((s, c) => s + c.qty, 0);

  const openProduct = (p: Product) => {
    const existing = cart.find((c) => c.sku === p.sku);
    setPending({ p, qty: existing ? existing.qty : 1, mode: existing ? "edit" : "add" });
  };

  return (
    <div className="grain min-h-screen w-full bg-paper flex" dir={isRtl ? "rtl" : undefined}>
      <aside className="w-60 bg-ink py-7 px-3 flex flex-col gap-3 shrink-0">
        <div className="px-3 pb-2">
          <p className="font-mono text-[10px] tracking-[0.4em] text-mango uppercase">Bar · To·Go</p>
          <h1 className="font-display font-black text-4xl text-paper leading-none mt-1">{t(lang, "menu_word")}</h1>
        </div>
        {CATEGORIES.map((c, i) => (
          <TicketStub
            key={c.id}
            number={`${["I","II","III","IV","V","VI","VII","VIII"][i] ?? i + 1} · ${c.emoji}`}
            label={c.name}
            caption={`${PRODUCTS.filter(p => p.category_id === c.id).length} ${t(lang, "menu_things")}`}
            active={currentCat === c.id}
            onClick={() => setActiveCat(c.id)}
            className="animate-rise"
          />
        ))}
        <div className="mt-auto px-3 pt-6">
          <div className="font-mono text-[9px] tracking-widest text-paper/40">{t(lang, "card_only_main")}</div>
          <div className="font-mono text-[9px] tracking-widest text-paper/40">{t(lang, "card_only_sub")}</div>
        </div>
      </aside>

      {/* products — board of lotería cards */}
      <main className="flex-1 px-8 py-8 overflow-auto pb-44">
        {/* utility row: restart + view order */}
        <div className="flex justify-end gap-2 mb-3">
          <button
            onClick={() => setConfirmRestart(true)}
            className="px-4 py-2 rounded-sm border-2 border-ink/30 hover:border-ink active:bg-ink active:text-paper font-mono text-xs tracking-widest uppercase"
          >
            ↺ {t(lang, "menu_restart")}
            {lang !== "es" && <span className="opacity-60"> · Reiniciar</span>}
            {lang !== "en" && <span className="opacity-60"> · Restart</span>}
          </button>
          <button
            onClick={() => navigate("/kiosk/review")}
            disabled={cart.length === 0}
            className="px-4 py-2 rounded-sm bg-ink text-paper disabled:opacity-30 font-mono text-xs tracking-widest uppercase active:scale-95 transition"
          >
            {t(lang, "menu_view_order")} ({totalQty}) · ${total}
          </button>
        </div>

        <div className="mb-6 flex items-end justify-between">
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-mezcal uppercase">{t(lang, "menu_table_of")}</p>
            <h2 className="font-display font-black text-6xl tracking-kerned leading-none">
              {menuLoaded ? CATEGORIES.find((c) => c.id === currentCat)?.name : "…"}
            </h2>
          </div>
          <div className="font-hand text-2xl text-cactus">— {t(lang, "menu_pick")} —</div>
        </div>

        <div className="grid grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-6">
          {items.map((p, i) => {
            const inCart = cart.find((c) => c.sku === p.sku);
            return (
              <div key={p.sku} className="relative animate-rise" style={{ animationDelay: `${i * 60}ms` }}>
                <LoteriaCard
                  number={i}
                  emblem={p.image}
                  title={localizedName(p, lang)}
                  caption={localizedDescription(p, lang)}
                  price={p.price}
                  rotate={CARD_ROT[i % CARD_ROT.length]}
                  onClick={() => openProduct(p)}
                  allergens={p.allergens}
                >
                  {inCart && (
                    <div
                      className="absolute -top-3 -left-3 w-14 h-14 rounded-full bg-cactus text-paper border-4 border-paper shadow-md flex items-center justify-center font-mono text-xl font-bold animate-thud"
                      style={{ transform: "rotate(-12deg)" }}
                    >
                      ×{inCart.qty}
                    </div>
                  )}
                </LoteriaCard>
              </div>
            );
          })}
        </div>
      </main>

      {/* receipt roll docked bottom-right */}
      <div
        className="fixed bottom-0 right-0 w-[28rem] z-30"
        onClick={() => setCartOpen((o) => !o)}
      >
        <ReceiptRoll items={cart} total={total} expanded={cartOpen} />
      </div>

      {/* checkout button when cart has items */}
      {cart.length > 0 && !joinTabId && (
        <button
          onClick={() => navigate("/kiosk/name")}
          className="fixed bottom-6 left-72 px-10 py-5 rounded-full bg-mezcal text-paper font-display text-2xl font-bold shadow-[0_12px_30px_-8px_rgba(200,75,26,0.7)] active:scale-95 z-30"
        >
          {t(lang, "menu_pay")} · ${total} →
        </button>
      )}

      {cart.length > 0 && joinTabId && (
        <button
          onClick={submitToTab}
          disabled={submittingJoin}
          className="fixed bottom-6 left-72 px-10 py-5 rounded-full bg-cactus text-paper font-display text-2xl font-bold shadow-[0_12px_30px_-8px_rgba(75,134,75,0.7)] active:scale-95 z-30 disabled:opacity-50"
        >
          {submittingJoin ? "Enviando…" : `Agregar a la cuenta · ${totalQty} items →`}
        </button>
      )}

      {joinTabId && (
        <div className="fixed top-3 left-1/2 -translate-x-1/2 z-40 bg-cactus text-paper px-4 py-2 rounded font-mono text-xs tracking-widest uppercase shadow-md">
          🔗 Mesa · {joinTabId.slice(-6)}
        </div>
      )}

      {/* qty / edit sheet */}
      {pending && (
        <div
          className="fixed inset-0 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-6 z-40"
          onClick={() => setPending(null)}
        >
          <div
            className="bg-paper rounded-sm border-4 border-ink max-w-md w-full p-6 relative animate-rise"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute -top-3 left-6 bg-paper px-3 font-mono text-[10px] tracking-widest">
              {pending.mode === "edit" ? t(lang, "menu_edit_label") : t(lang, "menu_add_label")} · № {pending.p.sku}
            </div>
            <div className="text-center mb-3">
              {pending.p.image?.startsWith("/api/img/") || pending.p.image?.startsWith("http")
                ? <img src={pending.p.image} className="w-32 h-32 object-cover rounded-xl mx-auto border-2 border-ink" />
                : <span className="text-7xl">{pending.p.image}</span>}
            </div>
            <h3 className="font-display font-black text-3xl text-center leading-tight">{localizedName(pending.p, lang)}</h3>
            <p className="text-center text-ink/60 font-sans mt-1 px-4">{localizedDescription(pending.p, lang)}</p>
            <div className="text-center font-mono text-3xl font-bold text-mezcal mt-3">${pending.p.price}</div>

            <div className="flex items-center justify-center gap-6 my-6">
              <button
                onClick={() => setPending({ ...pending, qty: Math.max(1, pending.qty - 1) })}
                className="w-16 h-16 rounded-full border-2 border-ink text-3xl font-bold active:bg-ink active:text-paper"
              >
                −
              </button>
              <span className="font-display text-6xl font-black w-16 text-center tabular-nums">{pending.qty}</span>
              <button
                onClick={() => setPending({ ...pending, qty: pending.qty + 1 })}
                className="w-16 h-16 rounded-full border-2 border-ink text-3xl font-bold active:bg-ink active:text-paper"
              >
                +
              </button>
            </div>

            {pending.mode === "edit" && (
              <button
                onClick={() => { setQty(pending.p.sku, 0); setPending(null); }}
                className="w-full py-3 mb-3 rounded-sm border-2 border-hibisco text-hibisco font-display font-bold tracking-wide"
              >
                ✕ {t(lang, "menu_remove")}
              </button>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setPending(null)}
                className="flex-1 py-4 rounded-sm border-2 border-ink/30 font-display text-lg font-bold"
              >
                {t(lang, "cancel")}
              </button>
              <button
                onClick={() => {
                  if (pending.mode === "edit") setQty(pending.p.sku, pending.qty);
                  else addToCart(pending.p, pending.qty);
                  setPending(null);
                }}
                className="flex-[2] py-4 rounded-sm bg-ink text-paper font-display text-lg font-bold tracking-wide"
              >
                {pending.mode === "edit" ? t(lang, "menu_update") : t(lang, "menu_add")} · ${pending.p.price * pending.qty}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* restart confirmation */}
      {confirmRestart && (
        <div
          className="fixed inset-0 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-6 z-50"
          onClick={() => setConfirmRestart(false)}
        >
          <div
            className="bg-paper rounded-sm border-4 border-ink max-w-md w-full p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-5xl mb-3">↺</div>
            <h3 className="font-display font-black text-3xl">{t(lang, "restart_title")}</h3>
            <p className="font-sans text-ink/60 mt-2">{t(lang, "restart_body")}</p>
            {lang !== "es" && lang !== "en" && (
              <p className="font-mono text-[10px] tracking-widest text-ink/50 mt-2 uppercase">
                Reiniciar · Restart
              </p>
            )}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setConfirmRestart(false)}
                className="flex-1 py-4 rounded-sm border-2 border-ink/30 font-display text-lg font-bold"
              >
                {t(lang, "cancel")}
                {lang !== "es" && lang !== "en" && (
                  <div className="font-mono text-[9px] tracking-widest text-ink/50 mt-0.5">Cancelar · Cancel</div>
                )}
              </button>
              <button
                onClick={() => { reset(); navigate("/kiosk"); }}
                className="flex-1 py-4 rounded-sm bg-hibisco text-paper font-display text-lg font-bold"
              >
                {t(lang, "restart_yes")}
                {lang !== "es" && lang !== "en" && (
                  <div className="font-mono text-[9px] tracking-widest text-paper/70 mt-0.5">Sí, reiniciar · Yes, restart</div>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
